// Firestore access for the fixed-fee client statement (CPBM) and its public,
// login-free share link. Editors are admin@almhr.com and nazim@almhr.com
// (firestore.rules); the share document is readable by anyone who holds its
// unguessable token, and can only be listed/written by editors.
import { collection, deleteDoc, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import { auth, db } from "../firebase";
import { normalizeStatement, validateStatement } from "./fixedFeeCalculator";

const STATEMENTS = "fixed_fee_statements";
const SHARES = "public_shares";
const SNAPSHOTS = "approved_invoice_snapshots";

export const FIXED_FEE_EDITORS = ["admin@almhr.com", "nazim@almhr.com"];
export const canEditFixedFee = () => FIXED_FEE_EDITORS.includes(auth.currentUser?.email?.toLowerCase() || "");

const statementId = (clientCode, period) => `${clientCode}_${period}`;

export async function listStatements(clientCode) {
  const result = await getDocs(query(collection(db, STATEMENTS), where("clientCode", "==", clientCode)));
  return result.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function saveStatement(statement) {
  const problem = validateStatement(statement);
  if (problem) throw new Error(problem);
  const clean = normalizeStatement(statement);
  await setDoc(doc(db, STATEMENTS, statementId(clean.clientCode, clean.period)), {
    ...clean, updatedAt: serverTimestamp(), updatedByEmail: auth.currentUser?.email || null
  }, { merge: true });
  return clean;
}

export async function deleteStatement(clientCode, period) {
  await deleteDoc(doc(db, STATEMENTS, statementId(clientCode, period)));
}

// The month's fixed fee as actually invoiced (service-charge subtotal, i.e.
// before tax) from the approved invoice snapshots.
export async function fetchInvoicedFee(clientCode, period) {
  const result = await getDocs(query(collection(db, SNAPSHOTS), where("clientCode", "==", clientCode)));
  const matches = result.docs.map((item) => item.data()).filter((data) => data.accountingPeriod === period && data.status === "APPROVED");
  return {
    amount: matches.reduce((sum, data) => sum + Number(data.totals?.serviceChargeSubtotal || 0), 0),
    invoiceNumbers: matches.map((data) => data.invoiceNumber)
  };
}

function newToken() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function getShare(clientCode) {
  const result = await getDocs(query(collection(db, SHARES), where("clientCode", "==", clientCode)));
  const first = result.docs[0];
  return first ? { token: first.id, ...first.data() } : null;
}

function shareFields(clientCode, clientName, statements) {
  return {
    clientCode, clientName, enabled: true,
    statements: statements.map(normalizeStatement).sort((a, b) => a.period.localeCompare(b.period)),
    publishedAt: serverTimestamp(), publishedByEmail: auth.currentUser?.email || null
  };
}

// A new token replaces any earlier one, so the old link stops working.
export async function createShare(clientCode, clientName, statements) {
  const existing = await getDocs(query(collection(db, SHARES), where("clientCode", "==", clientCode)));
  await Promise.all(existing.docs.map((item) => deleteDoc(item.ref)));
  const token = newToken();
  await setDoc(doc(db, SHARES, token), shareFields(clientCode, clientName, statements));
  return token;
}

export async function publishShare(token, clientCode, clientName, statements) {
  await setDoc(doc(db, SHARES, token), shareFields(clientCode, clientName, statements));
}

export async function disableShare(token) {
  await deleteDoc(doc(db, SHARES, token));
}

// Used by the public page — no sign-in.
export async function loadPublicShare(token) {
  if (!/^[a-f0-9]{32}$/.test(String(token))) return null;
  const snapshot = await getDoc(doc(db, SHARES, token));
  return snapshot.exists() && snapshot.data().enabled ? snapshot.data() : null;
}
